import { capture, resetAnalytics } from './analytics';

export type SubscriberIdentity = {
  email: string;
  name: string;
  isSubscriber: boolean;
  subscriptionTier: string | null;
  subscriptionInterval: string | null;
};

let cachedIdentity: SubscriberIdentity | null = null;

/**
 * Returns the currently signed-in subscriber, or null when no one is
 * signed in. Return null for ANY failure mode: missing session, expired
 * session, malformed token, network error fetching the profile, etc. —
 * callers branch on null vs. identity and that's the whole contract.
 *
 * The value is backed by an in-memory cache that `refreshCurrentIdentity()`
 * keeps in sync with the backend session cookie.
 */
export function getCurrentIdentity(): SubscriberIdentity | null {
  return cachedIdentity;
}

export async function refreshCurrentIdentity(): Promise<SubscriberIdentity | null> {
  try {
    const res = await fetch('/api/auth/me', { headers: { Accept: 'application/json' } });
    if (!res.ok) {
      cachedIdentity = null;
      return null;
    }
    cachedIdentity = (await res.json()) as SubscriberIdentity;
    return cachedIdentity;
  } catch {
    cachedIdentity = null;
    return null;
  } finally {
    window.dispatchEvent(new CustomEvent(IDENTITY_CHANGED_EVENT));
  }
}

/**
 * Kick off the Lenny Access redirect flow. The backend owns state generation
 * and provider URL construction so browser code never sees auth secrets.
 */
export type LennyAccessPurpose = 'workshop' | 'course-discount';

export function startSignIn(purpose: LennyAccessPurpose = 'workshop'): void {
  capture('sign_in_started', { purpose });
  window.location.assign(signInUrl(false, purpose));
}

function signInUrl(refresh: boolean, purpose: LennyAccessPurpose): string {
  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const params = new URLSearchParams({ returnTo });
  if (refresh) params.set('refresh', '1');
  if (purpose === 'course-discount') params.set('purpose', purpose);
  return `/auth/lenny/start?${params.toString()}`;
}

/**
 * Clear stale local Lenny claims before sending the user through a new OAuth
 * transaction. Used when stored claims say the user is not currently eligible.
 */
export function refreshLennyStatus(purpose: LennyAccessPurpose = 'workshop'): void {
  capture('subscription_status_refresh_started', { purpose });
  cachedIdentity = null;
  window.location.assign(signInUrl(true, purpose));
}

/** Clear the server session and notify listeners. */
export async function signOut(): Promise<void> {
  capture('signed_out');
  try {
    await fetch('/api/auth/logout', { method: 'POST' });
  } finally {
    cachedIdentity = null;
    resetAnalytics();
    window.dispatchEvent(new CustomEvent(IDENTITY_CHANGED_EVENT));
  }
}

/** Event other components can subscribe to so the UI re-reads identity. */
export const IDENTITY_CHANGED_EVENT = 'identity-changed';
